// SPDX-License-Identifier: GPL-3.0-or-later
// The audio of the game page: the engine, the sound effects and the music, wired to the page.
//
// - The browser starts sound only after a user gesture. The layer calls `engine.unlock()` at the
//   first keydown, pointerdown, touchend and click. It stops listening when the context runs, or when
//   the browser has no WebAudio.
// - The settings go to the engine: sound and music on or off, and their volume. A change applies at
//   once.
// - The audio stops (the context suspends) while the game is paused, while a perk draft holds the
//   table and while the tab is hidden. It starts again when none of these is true.
// - A pause or a perk draft suspends only after the last sound has ended. The sound is heard in
//   full. A hidden tab suspends at once. A resume cuts the sounds that a suspend froze, so none
//   plays late. The plunger charge tone is one of them. It does not hold the suspend. The cut ends
//   it.
// - The music state follows the game: no game plays "attract", a ball in play plays "play", more
//   than one ball plays "multiball", a featured turn plays "featured" (it wins over multiball), and a
//   pause, a perk draft or the music setting "off" gives "off". The page calls `tick` in every
//   frame, also on the start screen, so the music moves on in time.
// - A perk draft gives the same audio state as the pause dialog. Neither one ducks the music.
//
// Audio is optional. Every meaningful sound has a text cue elsewhere (the callout, the popups, the
// HUD and the live region).

import type { GamePhase } from "../src/shared/contracts.ts";
import type { FeedbackBus, FeedbackSettings, SettingsStore } from "../src/shared/feedback/index.ts";
import { type AudioEngine, createAudioEngine } from "./audio/engine.ts";
import { createMusic, type Music, type MusicState } from "./audio/music.ts";
import { createSfx, type Sfx } from "./audio/sfx.ts";
import { isGameActive } from "./screens.ts";

/** What the music needs to know about the game. */
export interface MusicInput {
  readonly phase: GamePhase;
  readonly paused: boolean;
  readonly ballsInPlay: number;
  readonly featured: boolean;
}

/** The part of a document (or a window) that the layer listens to. */
export interface GestureTarget {
  readonly hidden: boolean;
  addEventListener(
    type: string,
    listener: () => void,
    options?: { readonly capture?: boolean; readonly passive?: boolean },
  ): void;
  removeEventListener(
    type: string,
    listener: () => void,
    options?: { readonly capture?: boolean; readonly passive?: boolean },
  ): void;
}

/** What the layer takes from the page. */
export interface AudioLayerDeps {
  readonly doc: GestureTarget;
  readonly bus: FeedbackBus;
  readonly settings: Pick<SettingsStore, "get" | "subscribe">;
  /** Make the audio context. Give null when the browser has no WebAudio. */
  readonly createContext: () => AudioContext | null;
}

/** The audio layer. */
export interface AudioLayer {
  /** Try to start the audio. Call it from a user gesture. Safe to call again. */
  unlock(): void;
  /** The game is paused (the pause dialog is open) or not. */
  setPaused(paused: boolean): void;
  /** A perk draft holds the table (true) or not. The audio stops while it holds, as for a pause. */
  setHeld(held: boolean): void;
  /** Move the music on. Call it in every frame. */
  tick(nowMs: number, game: MusicInput): void;
  /** The engine, for a test or a reader that needs the buses. */
  readonly engine: AudioEngine;
  /** The last music state that the layer asked for. Null before the first `tick`. */
  readonly musicWanted: MusicState | null;
  /** The sound effects, for a test or a reader that needs the voices. */
  readonly sfx: Sfx;
  /** Stop every listener and every sound. */
  dispose(): void;
}

/** The events that count as a gesture for the browser. */
export const UNLOCK_EVENTS: readonly string[] = ["keydown", "pointerdown", "touchend", "click"];

const LISTEN = { capture: true, passive: true } as const;

/**
 * The music state for the game. A pause, a held perk draft or the music setting "off" gives "off".
 * `held` is true while a perk draft holds the table.
 */
export function musicStateFor(game: MusicInput, musicOn: boolean, held = false): MusicState {
  if (!musicOn || game.paused || held) return "off";
  if (!isGameActive(game.phase)) return "attract";
  if (game.featured) return "featured";
  return game.ballsInPlay > 1 ? "multiball" : "play";
}

/** The parts of a window that make an audio context. */
export interface ContextHost {
  readonly AudioContext?: typeof AudioContext;
  readonly webkitAudioContext?: typeof AudioContext;
}

/** The context factory of a window: the standard class, or the old WebKit one, or null. */
export function audioContextFactory(host: ContextHost): () => AudioContext | null {
  return () => {
    const Ctor = host.AudioContext ?? host.webkitAudioContext;
    return Ctor === undefined ? null : new Ctor();
  };
}

/** The mutable state of one audio layer. */
interface LayerState {
  readonly deps: AudioLayerDeps;
  readonly engine: AudioEngine;
  readonly sfx: Sfx;
  readonly music: Music;
  paused: boolean;
  held: boolean;
  lastState: MusicState | null;
  listening: boolean;
  // True while a hold waits for the last sound. The context runs on until that sound ends.
  settling: boolean;
  // True while this layer has suspended the context. A resume then cuts the frozen sounds.
  suspended: boolean;
  // The unlock listener. It is set once, before any gesture can come.
  unlock: () => void;
  // The visibility listener.
  sync: () => void;
}

function audioLayerApply(st: LayerState, s: FeedbackSettings): void {
  st.engine.setSfx(s.soundEnabled, s.volume);
  st.engine.setMusic(s.musicEnabled, s.musicVolume);
}

// A layer that already suspended does not suspend again. A second call would only repeat the
// first one, so a visibility change during a hold leaves the context as it is.
function audioLayerSuspend(st: LayerState): void {
  st.settling = false;
  if (st.suspended) return;
  st.suspended = true;
  st.engine.suspend();
}

function audioLayerResume(st: LayerState): void {
  st.settling = false;
  if (st.suspended) {
    // The suspend froze the sounds in flight. Cut them, so none plays after the resume.
    st.sfx.cut();
    st.suspended = false;
  }
  st.engine.resume();
}

// True while the context runs and a sound still sounds. A hold must wait for that sound.
function audioLayerSoundSounding(st: LayerState): boolean {
  const ctx = st.engine.context();
  return ctx?.state === "running" && st.sfx.tailEnd() > ctx.currentTime;
}

function audioLayerSync(st: LayerState): void {
  if (st.deps.doc.hidden) audioLayerSuspend(st);
  else if (!st.paused && !st.held) audioLayerResume(st);
  else if (audioLayerSoundSounding(st)) st.settling = true;
  else audioLayerSuspend(st);
}

// A hold that waits for a sound suspends the context once that sound has ended.
function audioLayerSettle(st: LayerState): void {
  if (!st.settling) return;
  const now = st.engine.context()?.currentTime ?? 0;
  if (st.sfx.tailEnd() > now) return;
  audioLayerSuspend(st);
}

function audioLayerStopListening(st: LayerState): void {
  if (!st.listening) return;
  st.listening = false;
  for (const type of UNLOCK_EVENTS) st.deps.doc.removeEventListener(type, st.unlock, LISTEN);
}

function audioLayerUnlock(st: LayerState): void {
  void st.engine.unlock().then(() => {
    // No context (no WebAudio) or a running one: no later gesture can change it.
    if (!st.engine.ready || st.engine.context()?.state === "running") audioLayerStopListening(st);
  });
}

function audioLayerTick(st: LayerState, nowMs: number, game: MusicInput): void {
  audioLayerSettle(st);
  const next = musicStateFor(game, st.deps.settings.get().musicEnabled, st.held);
  if (next !== st.lastState) {
    st.lastState = next;
    st.music.setState(next);
  }
  st.music.update(nowMs);
}

function audioLayerDispose(st: LayerState, stopSettings: () => void): void {
  audioLayerStopListening(st);
  st.deps.doc.removeEventListener("visibilitychange", st.sync);
  stopSettings();
  st.sfx.dispose();
  st.music.dispose();
  audioLayerSuspend(st);
}

/** Make the audio layer. */
export function createAudioLayer(deps: AudioLayerDeps): AudioLayer {
  const { doc, settings } = deps;
  const engine = createAudioEngine({ createContext: deps.createContext });
  const st: LayerState = {
    deps,
    engine,
    sfx: createSfx(engine, deps.bus),
    music: createMusic(engine, deps.bus),
    paused: false,
    held: false,
    lastState: null,
    listening: false,
    settling: false,
    suspended: false,
    unlock: () => undefined,
    sync: () => undefined,
  };
  st.unlock = () => audioLayerUnlock(st);
  st.sync = () => audioLayerSync(st);
  audioLayerApply(st, settings.get());
  const stopSettings = settings.subscribe((s) => audioLayerApply(st, s));
  for (const type of UNLOCK_EVENTS) doc.addEventListener(type, st.unlock, LISTEN);
  st.listening = true;
  doc.addEventListener("visibilitychange", st.sync);
  return {
    unlock: st.unlock,
    engine,
    get musicWanted() {
      return st.lastState;
    },
    sfx: st.sfx,
    setPaused(next) {
      if (next === st.paused) return;
      st.paused = next;
      st.sync();
    },
    setHeld(next) {
      if (next === st.held) return;
      st.held = next;
      st.sync();
    },
    tick: (nowMs, game) => audioLayerTick(st, nowMs, game),
    dispose: () => audioLayerDispose(st, stopSettings),
  };
}
